import { PRIMARY_FLOW_PAYLOAD_TIMELINE_TYPE } from 'flare-chat-core/contracts/timeline/item-types';

function coerceObject(value = null) {
  return (value && typeof value === 'object' && !Array.isArray(value)) ? value : {};
}

function objectOrNull(value = null) {
  return (value && typeof value === 'object' && !Array.isArray(value)) ? value : null;
}

function normalizeText(value = '') {
  return String(value || '').trim();
}

export function resolveComposerUiProjection(payload = {}) {
  const source = coerceObject(payload);
  const primaryFlow = coerceObject(source.primary_flow);
  return objectOrNull(source.composer_ui) || objectOrNull(primaryFlow.composer_ui) || {};
}

function latestPrimaryFlowItem(timelineItems) {
  const items = Array.isArray(timelineItems) ? timelineItems : [];
  return [...items]
    .reverse()
    .find((entry) => normalizeText(entry?.type) === PRIMARY_FLOW_PAYLOAD_TIMELINE_TYPE) ?? {};
}

export function selectLatestComposerUiProjection({ timelineItems } = {}) {
  const item = latestPrimaryFlowItem(timelineItems);
  return resolveComposerUiProjection(coerceObject(item.payload));
}

export function selectLatestComposerModeSyncProjection({ timelineItems } = {}) {
  const item = latestPrimaryFlowItem(timelineItems);
  const payload = coerceObject(item.payload);
  const primaryFlow = coerceObject(payload.primary_flow);
  const composerUi = resolveComposerUiProjection(payload);
  return {
    activeMode: normalizeText(composerUi.active_mode),
    intakeSessionCompleted: primaryFlow.intake_session_completed === true,
    projectionKey: normalizeText(item.id || item.roundKey || item.round_key),
    source: normalizeText(composerUi.source),
  };
}

export function resolveRecommendationPanelProjection(composerUi = {}) {
  return coerceObject(coerceObject(composerUi).recommendation_panel);
}

export function resolveComposerChooserProjection(composerUi = {}) {
  const source = coerceObject(composerUi);
  return objectOrNull(source.composer_chooser) || objectOrNull(source.chooser_state) || {};
}

export function isAuthoritativeProjection(projection = {}) {
  return normalizeText(projection?.source) === 'authoritative';
}

// Public input-kind translation only; Kernel owns input admission and Run progression.
const INPUT_PRESENTATIONS = new Map([
  ['free_text', { kind: 'text' }],
  ['single_choice', { kind: 'chooser', selectionMode: 'single' }],
  ['multi_choice', { kind: 'chooser', selectionMode: 'multiple' }],
]);
const ORDINARY_INPUT = Object.freeze({ kind: 'ordinary', inputRequest: null, composerChooser: null, questionText: '' });
const inputError = () => Object.assign(new Error('Input request is not current'), { code: 'input_request_stale' });
const nonemptyText = (value) => typeof value === 'string' && value.trim().length > 0;
const validOptions = (options) => Array.isArray(options) && options.length > 0
  && options.every((option) => ['key', 'label', 'value'].every((key) => nonemptyText(option?.[key])))
  && new Set(options.map(({ key }) => key)).size === options.length;
const INPUT_PARAMETER_VALIDATORS = new Map([
  ['free_text', ({ options }) => Array.isArray(options) && options.length === 0],
  ['single_choice', ({ options }) => validOptions(options)],
  ['multi_choice', ({ options }) => validOptions(options)],
]);
const INPUT_BINDING_VALIDATORS = new Map([
  ['conversation', (request) => request.parameters.length === 1
    && !request.action_key && !request.selected_capability_id],
  ['action', (request) => nonemptyText(request.action_key) && nonemptyText(request.selected_capability_id)],
]);
const validInputParameter = (parameter, key) => {
  const validate = INPUT_PARAMETER_VALIDATORS.get(parameter?.kind);
  if (!validate) return false;
  return parameter.required === true && nonemptyText(parameter.field_key)
    && nonemptyText(parameter.label) && parameter.field_key === key && validate(parameter) === true;
};
const validInputRevision = (request) => Number.isSafeInteger(request.expected_context_revision)
  && request.expected_context_revision >= 0
  && typeof request.input_contract_digest === 'string'
  && /^sha256:[0-9a-f]{64}$/.test(request.input_contract_digest);
const validCurrentParameter = (request, sessionId) => {
  const parameters = Array.isArray(request.parameters) ? request.parameters : [];
  const missingInputs = Array.isArray(request.missing_inputs) ? request.missing_inputs : [];
  // The public Auto contract serializes its default Action binding by omission.
  const validate = INPUT_BINDING_VALIDATORS.get(request.input_binding ?? 'action');
  if (!validate || !parameters.length || parameters.length !== missingInputs.length) return false;
  return [
    request.schema_version === 1, request.scope === 'auto', request.session_id === sessionId,
    ['run_id', 'turn_id', 'decision_id', 'interaction_id'].every((key) => nonemptyText(request[key])),
    validInputRevision(request),
    new Set(missingInputs).size === missingInputs.length, validate(request),
    parameters.every((parameter, index) => validInputParameter(parameter, missingInputs[index])),
  ].every(Boolean);
};
const INPUT_PAGE_PRESENTATIONS = new Map([
  ['free_text', { inputKind: 'free_text', selectionMode: 'single' }],
  ['single_choice', { inputKind: 'single_choice', selectionMode: 'single' }],
  ['multi_choice', { inputKind: 'multi_choice', selectionMode: 'multiple' }],
]);
const inputPage = (parameter) => ({
  ...INPUT_PAGE_PRESENTATIONS.get(parameter.kind),
  fieldKey: parameter.field_key, title: parameter.label,
  allowOtherInput: parameter.allow_other_input === true,
  options: parameter.options.map(({ key, label, value }) => ({ key, text: label, answerValue: value })),
});
const choiceInputView = (request, parameter, presentation) => ({
  kind: presentation.kind, inputRequest: request, fieldKey: parameter.field_key, questionText: parameter.label,
  composerChooser: {
    kind: 'action_input', source: 'authoritative', visible: true, dismissible: request.scope === 'auto',
    key: [request.session_id, request.run_id, request.interaction_id,
      request.expected_context_revision ?? request.checkpoint_revision, request.input_contract_digest].join(':'),
    input_request: request, totalPages: request.parameters.length,
    pages: request.parameters.map(inputPage),
  },
});
const INPUT_VIEW_RENDERERS = new Map([
  ['text', (request, parameter) => ({ kind: 'text', inputRequest: request,
    fieldKey: parameter.field_key, questionText: parameter.label, composerChooser: null })],
  ['chooser', choiceInputView],
]);

const workflowIdentity = (request) => [request.run_id, request.workflow_job_id, request.plan_id,
  request.step_id, request.action_id, request.action_key, request.interaction_id,
  request.workflow_input_snapshot_id].every(nonemptyText);
const validWorkflowParameter = (request, workflow, sessionId) => request.schema_version === 1
  && workflow.session_id === sessionId && request.run_id === workflow.run_id && workflowIdentity(request)
  && Number.isSafeInteger(request.checkpoint_revision) && request.checkpoint_revision >= 2
  && typeof request.input_contract_digest === 'string' && /^sha256:[0-9a-f]{64}$/.test(request.input_contract_digest)
  && Array.isArray(request.missing_inputs) && request.missing_inputs.length > 0
  && new Set(request.missing_inputs).size === request.missing_inputs.length
  && Array.isArray(request.parameters) && request.parameters.length === request.missing_inputs.length
  && request.parameters.every((parameter, index) => validInputParameter(parameter, request.missing_inputs[index]));
const invalidInput = (request, code = 'input_request_stale') => ({ kind: 'invalid', inputRequest: request,
  composerChooser: null, questionText: '', error: Object.assign(inputError(), { code }) });
const requestIdentity = (request) => JSON.stringify([request.run_id, request.interaction_id,
  request.expected_context_revision, request.checkpoint_revision, request.input_contract_digest]);

/** Consume accepted current owner projections; absence never revives a historical question. */
export const selectCurrentComposerInputProjection = ({ sessionId, sessionContext, run } = {}) => {
  const workflow = objectOrNull(sessionContext?.workflow_projection);
  const ownsWorkflow = workflow && (!run?.run_id || workflow.run_id === run.run_id);
  const hasWorkflowInput = ownsWorkflow && Object.hasOwn(workflow, 'input_request');
  const request = objectOrNull(hasWorkflowInput ? workflow.input_request : sessionContext?.input_request);
  const waiting = (ownsWorkflow ? workflow.run_state : run?.state) === 'waiting_for_user';
  if (!request || Object.keys(request).length === 0) {
    return waiting ? invalidInput(null, 'input_request_unavailable') : ORDINARY_INPUT;
  }
  if (run?.run_id && (request.run_id !== run.run_id || ['completed', 'failed', 'cancelled'].includes(run.state))) {
    return invalidInput(request);
  }
  const mirrored = objectOrNull(sessionContext?.input_request);
  if (hasWorkflowInput && mirrored && requestIdentity(request) !== requestIdentity(mirrored)) return invalidInput(request);
  if (ownsWorkflow && (workflow.session_id && workflow.session_id !== sessionId
    || ['completed', 'failed', 'cancelled'].includes(workflow.run_state))) return invalidInput(request);
  const protocol = request.scope === 'auto' ? 'auto' : 'workflow';
  const valid = protocol === 'auto' ? validCurrentParameter(request, sessionId)
    : hasWorkflowInput && validWorkflowParameter(request, workflow, sessionId);
  if (!valid) return invalidInput(request);
  const captured = structuredClone(request);
  const parameter = captured.parameters[0];
  const presentation = INPUT_PRESENTATIONS.get(parameter.kind);
  const viewKind = protocol === 'auto' || request.parameters.length > 1 ? 'chooser' : presentation.kind;
  const view = INPUT_VIEW_RENDERERS.get(viewKind)(captured, parameter, { ...presentation, kind: viewKind });
  return { ...view, protocol, requestKey: requestIdentity(captured) };
};


const SUBMISSION_PRESENTATIONS = new Map([
  ['completed', { status: 'submitted', interactive: false, pending: false }],
  ['failed', { status: 'failed', interactive: true, pending: false }],
  ['blocked', { status: 'failed', interactive: true, pending: false }],
  ['partial', { status: 'unconfirmed', interactive: false, pending: false }],
  ['aborted', { status: 'unconfirmed', interactive: false, pending: false }],
]);
const STALE_INPUT_ERRORS = new Set(['input_request_stale', 'confirmation_request_stale', 'AUTO_INPUT_CONFLICT']);
const normalizeSubmissionResult = (result) => {
  if (result instanceof Error) return { status: 'failed', error: result };
  if (!result || typeof result !== 'object') return { status: 'failed' };
  return result;
};
const submissionPresentation = (result) => {
  const { status, error, reason } = normalizeSubmissionResult(result);
  const presentation = SUBMISSION_PRESENTATIONS.get(status) ?? SUBMISSION_PRESENTATIONS.get('partial');
  const current = STALE_INPUT_ERRORS.has(error?.code ?? reason) ? SUBMISSION_PRESENTATIONS.get('partial') : presentation;
  return { ...current, errorMessage: error?.message ?? '' };
};
const PRESENTATION_TRANSITIONS = new Map([
  ['submit', (state, { key, attempt }) => new Map(state).set(key, {
    attempt, status: 'submitting', interactive: false, pending: true, errorMessage: '',
  })],
  ['settle', (state, { key, attempt, result }) => {
    if (state.get(key)?.attempt !== attempt) return state;
    return new Map(state).set(key, { attempt, ...submissionPresentation(result) });
  }],
]);

/** Disclosure only: operation outcomes do not grant backend acceptance or completion. */
export const reduceInteractionPresentation = (state, event) => {
  const transition = PRESENTATION_TRANSITIONS.get(event.type);
  if (!transition || !event.key) throw new TypeError('Interaction presentation transition required');
  return transition(state, event);
};

/** Disclosure only: retained requests and backend acceptance are never rewritten here.
 * @param {import('./contracts').ComposerInputProjection} projection
 * @param {string} [dismissedInputKey]
 * @param {import('./contracts').InteractionPresentation | null} [presentation]
 * @returns {import('./contracts').ComposerInputProjection}
 */
export const resolveComposerInputPresentation = (projection, dismissedInputKey = '', presentation = null) => {
  const dismissed = dismissedInputKey && projection?.protocol === 'auto'
    && projection.composerChooser?.key === dismissedInputKey;
  const current = dismissed ? { ...projection, kind: 'message', composerChooser: null, questionText: '' } : projection;
  if (!current?.composerChooser || !presentation) return current;
  return { ...current, questionText: presentation.interactive ? current.questionText : '',
    composerChooser: { ...current.composerChooser, presentation, errorMessage: presentation.errorMessage } };
};
